/**
 * Verb tones — the shared vocabulary used by EvidenceSheet,
 * SuggestionRow, FilterChips, and any other surface that renders a
 * verb pill or polarity-coded chrome.
 *
 * Translated from the Tailwind palette in design/components/EvidenceSheet.jsx
 * so the web and design files stay visually identical. Keep these in
 * sync — the design files are the source of truth for tone.
 */
import {SuggestionVerb} from '../../../types/generated/graphql';

export type VerbToken = 'block' | 'follow' | 'boost' | 'skip';

export interface VerbTone {
  /** Imperative — "Block" / "Follow" / "Boost" / "Skip". */
  actionLabel: string;
  /** Past tense — "Blocked" / "Followed" / "Boosted" / "Skipped". */
  pastLabel: string;
  /** @deprecated Use actionLabel/pastLabel. Kept for back-compat. */
  label: string;
  pillBg: string;
  pillBorder: string;
  pillText: string;
  /**
   * Light-mode pill chrome — used on the Activity surface where the
   * background is white/slate-50. The dark-mode tokens above ride on
   * the Review surface's slate-950 background; reusing them on a
   * light surface renders the pale `text-rose-200` against white and
   * effectively erases the label. The light triple maps to the
   * tailwind {tone}-50 / {tone}-200 / {tone}-700 contrast formula.
   */
  pillBgLight: string;
  pillBorderLight: string;
  pillTextLight: string;
  accent: string;
  bar: string;
  bgSoft: string;
  primaryBg: string;
  primaryBgHover: string;
}

export const VERB_TONES: Record<VerbToken, VerbTone> = {
  block: {
    actionLabel: 'Block',
    pastLabel: 'Blocked',
    label: 'BLOCK',
    pillBg: 'rgba(244, 63, 94, 0.15)',
    pillBorder: 'rgba(251, 113, 133, 0.5)',
    pillText: '#FECDD3',
    pillBgLight: '#FFF1F2',
    pillBorderLight: '#FECDD3',
    pillTextLight: '#BE123C',
    accent: '#FDA4AF',
    bar: '#F43F5E',
    bgSoft: 'rgba(244, 63, 94, 0.1)',
    primaryBg: '#F43F5E',
    primaryBgHover: '#FB7185',
  },
  follow: {
    actionLabel: 'Follow',
    pastLabel: 'Followed',
    label: 'FOLLOW',
    pillBg: 'rgba(16, 185, 129, 0.15)',
    pillBorder: 'rgba(52, 211, 153, 0.5)',
    pillText: '#A7F3D0',
    pillBgLight: '#ECFDF5',
    pillBorderLight: '#A7F3D0',
    pillTextLight: '#047857',
    accent: '#6EE7B7',
    bar: '#10B981',
    bgSoft: 'rgba(16, 185, 129, 0.1)',
    primaryBg: '#10B981',
    primaryBgHover: '#34D399',
  },
  boost: {
    actionLabel: 'Boost',
    pastLabel: 'Boosted',
    label: 'BOOST',
    pillBg: 'rgba(99, 102, 241, 0.15)',
    pillBorder: 'rgba(129, 140, 248, 0.5)',
    pillText: '#C7D2FE',
    pillBgLight: '#EEF2FF',
    pillBorderLight: '#C7D2FE',
    pillTextLight: '#4338CA',
    accent: '#A5B4FC',
    bar: '#6366F1',
    bgSoft: 'rgba(99, 102, 241, 0.1)',
    primaryBg: '#6366F1',
    primaryBgHover: '#818CF8',
  },
  skip: {
    actionLabel: 'Skip',
    pastLabel: 'Skipped',
    label: 'SKIP',
    pillBg: 'rgba(100, 116, 139, 0.15)',
    pillBorder: 'rgba(148, 163, 184, 0.4)',
    pillText: '#E2E8F0',
    pillBgLight: '#F1F5F9',
    pillBorderLight: '#CBD5E1',
    pillTextLight: '#334155',
    accent: '#CBD5E1',
    bar: '#94A3B8',
    bgSoft: 'rgba(100, 116, 139, 0.1)',
    primaryBg: '#64748B',
    primaryBgHover: '#94A3B8',
  },
};

export function verbTokenForVerb(verb: SuggestionVerb): VerbToken {
  switch (verb) {
    case SuggestionVerb.Block:
      return 'block';
    case SuggestionVerb.Follow:
      return 'follow';
    case SuggestionVerb.Boost:
      return 'boost';
    case SuggestionVerb.Skip:
      return 'skip';
    default:
      return 'skip';
  }
}

/**
 * Score-to-tone — used by ScoreBadge inside the video evidence grid.
 * The 0-10 thresholds mirror the rest of the system.
 */
export function scoreTone(score: number): 'good' | 'warn' | 'bad' {
  if (score >= 7) return 'good';
  if (score >= 4) return 'warn';
  return 'bad';
}

export const SCORE_TONE_SX: Record<
  'good' | 'warn' | 'bad' | 'unscored',
  {bg: string; border: string; color: string}
> = {
  good: {bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(52, 211, 153, 0.5)', color: '#A7F3D0'},
  warn: {bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(251, 191, 36, 0.5)', color: '#FDE68A'},
  bad: {bg: 'rgba(244, 63, 94, 0.15)', border: 'rgba(251, 113, 133, 0.5)', color: '#FECDD3'},
  // Neutral slate — cold cache / metadata-only verdict. Reads as
  // "we haven't scored this yet" rather than as a good/warn/bad
  // signal. Matches the `skip` verb tone family.
  unscored: {bg: 'rgba(100, 116, 139, 0.15)', border: 'rgba(148, 163, 184, 0.4)', color: '#E2E8F0'},
};

export type AvatarTone = 'rose' | 'emerald' | 'indigo' | 'slate';

export const AVATAR_TONE_SX: Record<AvatarTone, {background: string; color: string}> = {
  rose: {background: 'linear-gradient(135deg, #FB7185 0%, #E11D48 100%)', color: '#FFFFFF'},
  emerald: {background: 'linear-gradient(135deg, #34D399 0%, #059669 100%)', color: '#FFFFFF'},
  indigo: {background: 'linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)', color: '#FFFFFF'},
  slate: {background: 'linear-gradient(135deg, #64748B 0%, #334155 100%)', color: '#FFFFFF'},
};

export function avatarToneForVerb(verb: VerbToken): AvatarTone {
  if (verb === 'block') return 'rose';
  if (verb === 'follow') return 'emerald';
  if (verb === 'boost') return 'indigo';
  return 'slate';
}
